//Library Imports
import { FC, useEffect, useMemo, useRef, useState } from "react";
import store, { RootState } from "../../../../../store";
import { useSelector } from "react-redux";

//Local Imports
import TableSort from "modules/Common/Components/TableSort";
import { getContentArticlesContacts, resetAnalyticsContent } from "../../action";
import { addCommaToNumber, getImageUrl, getQueryContactId } from "utils";
import { setSortDirection } from "modules/Analytics/utils";
import ContactListLoader from "../../ContactListLoader";
import { CONTACTS_INNER_ANALYTICS, DEFAULT_CAMPAIGN_IMAGE } from "constants/types";
import Speed from "../../../../../assets/img/icons/Speed.svg";
import { RedirectToSource } from "./utils";

//Style Imports
import styled from "styled-components";

interface IContactsContent {
  page?: string;
  tab?: string;
}

const DEFAULT_PAGE_LIMIT = 20;

const ContactsContent: FC<IContactsContent> = (props: IContactsContent) => {
  //Local States
  const [sortType, setSortType] = useState({ accessor: "", sortDirection: "none" });
  const [sortBy, setSortBy] = useState("");
  const [ImgWidth, setImgWidth] = useState<number>(0);
  const [ImgHeight, setImgHeight] = useState<number>(0);
  const ImgDimensionsRef = useRef<any>();

  //Redux states
  const content = useSelector((state: RootState) => state.contactInnerDetails.content);
  const loading = useSelector((state: RootState) => state.contactInnerDetails.contentLoading);
  const page = useSelector((state: RootState) => state.contactInnerDetails.contentPage);

  // This function calculates width and height of the list
  const getImgSize = () => {
    const newWidth = ImgDimensionsRef?.current?.clientWidth;
    setImgWidth(newWidth);

    const newHeight = ImgDimensionsRef?.current?.clientHeight;
    setImgHeight(newHeight);
  };

  // Update 'width' and 'height' when the window resizes
  useEffect(() => {
    getImgSize();
    window.addEventListener("resize", getImgSize);
    return () => window.removeEventListener("resize", getImgSize);
  }, []);

  let columnsData = useMemo(
    () => [
      {
        id: "main",
        hideHeader: true,
        disableSortBy: true,
        columns: [
          {
            id: "article_title",
            Header: "Article Title",
            accessor: "article_title",
            disableSortBy: true,
            Cell: (props: any) => {
              let item = props.row.original;
              return (
                <div className="hand-cursor">
                  <div className="preview">
                    <div
                      className="company-overlay"
                      style={{
                        backgroundImage: `url(${item.article_image !== null && item.article_image !== ""
                          ? getImageUrl(
                            item.article_image,
                            ImgWidth > 0 ? ImgWidth : 50,
                            ImgHeight > 0 ? ImgHeight : 50,
                            item.article_id
                          )
                          : DEFAULT_CAMPAIGN_IMAGE
                          })`,
                      }}
                    ></div>

                    <img
                      ref={ImgDimensionsRef}
                      src={
                        item.article_image !== null && item.article_image !== ""
                          ? getImageUrl(
                            item.article_image,
                            ImgWidth > 0 ? ImgWidth : 50,
                            ImgHeight > 0 ? ImgHeight : 50,
                            item.article_id
                          )
                          : DEFAULT_CAMPAIGN_IMAGE
                      }
                      alt=""
                      onError={(e: any) => {
                        e.target.onerror = null;
                      }}
                    />
                  </div>
                  <div className="details">
                    <div className="article_title">{item.article_title}</div>
                    <div className="article_link">
                      <a target="_blank" href={item.article_link} rel="noreferrer">
                        {item.article_link}
                      </a>
                    </div>
                  </div>
                </div>
              );
            },
          },
          {
            id: "sent_date",
            Header: "Email/ Sent",
            accessor: "email_name",
            sortType: "basic",
            sortDirection: sortType.accessor === "sent_date" ? sortType.sortDirection : "none",
            canSort: true,
            Cell: (props: any) => {
              let item = props.row.original;
              return (
                <div className="hand-cursor">
                  <div className="email_name">{item.email_name}</div>
                  <div className="email_sent_date">{item.email_sent_date}</div>
                </div>
              );
            },
          },
          {
            id: "total_clicks",
            Header: "Clicks",
            accessor: "total_clicks",
            sortType: "basic",
            sortDirection: sortType.accessor === "total_clicks" ? sortType.sortDirection : "none",
            canSort: true,
            Cell: (props: any) => {
              let item = props.row.original;
              return addCommaToNumber((item.total_clicks ?? 0).toString() ?? "");
            },
          },
          {
            id: "last_click_date",
            Header: "Click Date",
            accessor: "last_click_date",
            sortType: "basic",
            sortDirection: sortType.accessor === "last_click_date" ? sortType.sortDirection : "none",
            canSort: true,
            Cell: (props: any) => {
              let item = props.row.original;
              let click_date = "";
              let click_time = "";
              if (item.last_click_date !== "") {
                click_date =
                  item.last_click_date.split(", ")[0] + ", " + item.last_click_date.split(", ")[1].split(" ")[0];
                click_time =
                  item.last_click_date.split(", ")[1].split(" ")[1] +
                  " " +
                  item.last_click_date.split(", ")[1].split(" ")[2];
              }
              return (
                <div className="hand-cursor">
                  <div className="email_sent_date">{click_date}</div>
                  <div className="email_sent_date">{click_time}</div>
                </div>
              );
            },
          },
          {
            id: "score",
            Header: "Score",
            accessor: "score",
            disableSortBy: true,
            Cell: (props: any) => {
              let item = props.row.original;
              return (
                <div className="last-item hand-cursor">
                  <div className="engagement">
                    <img src={Speed} alt="" />
                    &nbsp;
                    {addCommaToNumber((item.score ?? 0).toString() ?? "")}
                  </div>
                </div>
              );
            },
          },
        ],
      },
    ],
    [sortType]
  );

  let timeoutId: any;

  const debounce = (callback: any, wait: any, column: any) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    timeoutId = setTimeout(() => {
      callback(column);
    }, wait);
  };

  const sortedResults = (column?: any) => {
    let sort_type = "";
    if (column.canSort) {
      setSortBy(column.id);
      sort_type = setSortDirection(column.id, column.sortDirection);
      setSortType({ accessor: column.id, sortDirection: sort_type });
      store.dispatch({
        type: CONTACTS_INNER_ANALYTICS.CONTENT_RESET,
        payload: { sortBy: column.id, sortType: sort_type },
      });
      getContentArticlesContacts(getPagination({ sortType: sort_type, sortBy: column.id, resetOffset: true }));
    }
  };
  const getPagination = (params?: any) => {
    return {
      contact_id: getQueryContactId(),
      limit: DEFAULT_PAGE_LIMIT,
      offset: params?.resetOffset ? 0 : page > 0 ? content.length : 0,
      sort_by: params !== undefined ? params.sortBy : sortBy,
      sort_type: params !== undefined ? params.sortType : sortType.sortDirection,
    };
  };

  const debouncedSorting = (column?: any) => {
    debounce(sortedResults, 1000, column);
  };


  useEffect(() => {
    if (page === 0) {
      getContentArticlesContacts(getPagination());
    }
    return () => {
      if (!window.location.pathname.includes('contacts/')) resetAnalyticsContent();
    };
  }, []);
  return (
    <Wrapper className="contact-engagement-user">
      <div>
        <TableSort
          columns={columnsData}
          data={content}
          module={"contacts_content"}
          _onCellClick={debouncedSorting}
          _onRowClick={RedirectToSource}
          next={() => getContentArticlesContacts(getPagination())}
          hasMore={true}
          loader={<ContactListLoader loading={loading} />}
        />
        {!loading && content !== undefined && Object.keys(content).length === 0 ? (
          <div className="no-data">No Data Found!</div>
        ) : null}
      </div>
    </Wrapper>
  );
};

export default ContactsContent;

const Wrapper = styled.div`
  overflow: hidden;
  width: 100%;
  margin-bottom: 10px;
  display: flex;
  flex-direction: row;
  align-items: center;
  @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
    min-height: 83px;
  }
  .engagement {
    border-radius: 12px;
    background-color: #1971eb;
    color: #ffffff;
    font-size: 16px;
    font-family: "robotomedium";
    font-weight: 500;
    padding: 0px 10px;
    display: inline;
    float: right;
    white-space: nowrap;
  }
  .hand-cursor {
    cursor: pointer;
  }
  .no-data {
    width: 100%;
    height: 100px;
    display: flex;
    justify-content: center;
    align-items: center;
    font-family: "robotomedium";
    color: #67696a;
    font-size: 1rem;
    margin-bottom: 30px;
    border-radius: 5px;
  }
  > div {
    width: 100%;
  }
  table {
    width: 100%;
    margin-top: 15px;
    display: block;
    thead {
      border-bottom: 1px solid #dddddd;
      tr {
        padding: 0 15px;
        th {
          color: #5e7079;
          font-weight: 700;
          font-size: 18px;
          font-family: "Roboto", sans-serif;
        }
      }
    }
    tbody {
      width: 100%;
      display: block;
      box-sizing: border-box;
      overflow-y: auto;
      height: calc(100vh - 200px);
      @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
        height: calc(100vh - 200px);
      }
      scrollbar-width: thin;
      scrollbar-color: #001760 #efefef;
      &::-webkit-scrollbar {
        width: 5px;
        height: 5px;
        border-radius: 5px;
      }
      /* Track */
      &::-webkit-scrollbar-track {
        background: #fff;
        margin-top: 0;
        margin-bottom: 10px;
        @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
          margin-top: 0;
        }
      }
      /* Handle */
      &::-webkit-scrollbar-thumb {
        background: #001760;
      }
      > div {
        overflow: hidden;
      }
      tr {
        background-color: #ffffff;
        box-shadow: 0px 0px 17px -2px rgba(0, 0, 0, 0.08);
        width: 100%;
        border-radius: 8px;
        transition-duration: 0.2s;
        position: relative;
        margin: 4px 0;
        padding: 16px 15px 10px 15px;
        cursor: pointer;
      }
    }
    thead {
      tr {
        th {
          span {
            width: 15px;
            .sort_asc {
              width: 10px;
              height: 8px;
              margin-top: -15px;
              margin-left: 5px;
              float: left;
              @media all and (max-width: 1439px), (-webkit-device-pixel-ratio: 1.25) {
                margin-top: -15px;
              }
              img {
                transform: rotate(180deg);
              }
            }
            .sort_desc {
              width: 10px;
              height: 8px;
              float: left;
              margin-left: 5px;
              margin-top: 5px;
              img {
                float: left;
              }
            }
          }
        }
      }

      tr:nth-child(2) {
        th {
          font-size: 16px;
          color: #5e7079;
          font-weight: 500;
          font-family: "Roboto", sans-serif;
          white-space: nowrap;
        }
      }
    }
    thead,
    tbody {
      width: 100%;
      display: block;
      float: left;
      tr {
        width: 100%;
        display: block;
        float: left;
        td {
          min-height: 70px;
          font-family: "Roboto", sans-serif;
          font-weight: 500;
          font-size: 20px;
          @media all and (max-width: 1439px), (-webkit-device-pixel-ratio: 1.25) {
            font-size: 14px;
          }
        }
        th,
        td {
          display: block;
          float: left;
          &:nth-child(1) {
            width: calc(100% - 900px);
            text-align: left;
            display: flex;
            @media all and (max-width: 1439px), (-webkit-device-pixel-ratio: 1.25) {
              width: calc(100% - 490px);
            }
            > div {
              display: flex;
              width: 100%;
              flex-direction: row;
              align-items: start;
              align-content: space-evenly;
              min-height: 100%;
              max-width: 100%;
              max-width: 100%;
              .preview {
                float: left;
                align-self: center;
                border-radius: 10px;
                overflow: hidden;
                padding: 2px;
                position: relative;
                img {
                  border-radius: 10px;
                  box-shadow: #f0b520 0px 0px 0px 2px;
                  min-width: 51px;
                  min-height: 51px;
                  width: 51px;
                  height: 51px;
                }
                .company-overlay {
                  min-width: 51px;
                  min-height: 51px;
                  border-radius: 10px;
                  position: absolute;
                  left: 2px;
                  top: 2px;
                  background-size: cover;
                  background-color: transparent;
                  background-repeat: no-repeat;
                  background-position: center;
                  z-index: 5;
                }
              }
              .details {
                float: left;
                font-weight: 400;
                color: #000000;
                font-size: 20px;
                font-family: "Roboto Slab", serif;
                align-self: center;
                max-width: calc(100% - 120px);
                .article_title {
                  color: #424b50;
                  font-weight: 500;
                  font-size: 16px;
                  font-family: "Roboto", sans-serif;
                  max-width: 100%;
                  display: -webkit-box;
                  -webkit-line-clamp: 2;
                  -webkit-box-orient: vertical;
                  overflow: hidden;
                  text-overflow: ellipsis;
                  word-break: break-word;
                }
                .article_link {
                  color: #1ec252;
                  font-weight: 500;
                  font-size: 14px;
                  font-family: "Roboto", sans-serif;
                  max-width: 100%;
                  overflow: hidden;
                  text-overflow: ellipsis;
                  white-space: nowrap;
                  display: block;
                }
              }
            }
          }
          &:nth-child(2) {
            width: 320px;
            text-align: left;
            display: inline-flex;
            align-items: center;
            @media all and (max-width: 1439px), (-webkit-device-pixel-ratio: 1.25) {
              width: 140px;
            }
            > div {
              display: flex;
              width: 100%;
              flex-direction: column;
              align-items: start;
              align-content: space-evenly;
              min-height: 100%;
              .email_name {
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
                word-break: keep-all;
                display: block;
                font-size: 16px;
                color: #424b50;
                max-width: 100%;
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
                @media all and (max-width: 1439px), (-webkit-device-pixel-ratio: 1.25) {
                  font-size: 14px;
                }
              }
              .email_sent_date {
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
                word-break: keep-all;
                display: block;
                font-size: 14px;
                color: #424b50;
                @media all and (max-width: 1439px), (-webkit-device-pixel-ratio: 1.25) {
                  font-size: 10px;
                }
              }
            }
          }
          &:nth-child(3) {
            width: 160px;
            text-align: center;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            @media all and (max-width: 1621px) {
              width: 110px;
            }
          }
          &:nth-child(4) {
            width: 300px;
            text-align: center;
            display: inline-flex;
            align-items: center;
            &[role="cell"] {
              font-size: 14px;
              color: #424b50;
              @media all and (max-width: 1439px), (-webkit-device-pixel-ratio: 1.25) {
                font-size: 10px;
              }
            }
            @media all and (max-width: 1439px), (-webkit-device-pixel-ratio: 1.25) {
              width: 160px;
            }
          }
          &:nth-child(5) {
            width: 120px;
            text-align: center;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            @media all and (max-width: 1439px), (-webkit-device-pixel-ratio: 1.25) {
              width: 80px;
            }
          }
        }
      }
    }
  }

  .preview {
    position: relative;
    max-width: 55px;
    -ms-flex-negative: 1 !important;
    flex-shrink: 1 !important;
    img {
      border-radius: 100%;
      box-shadow: ${(props) => (props.color ? `0px 0px 0px 2px ${props.color}` : "")};
      max-width: 100%;
      height: 55px;
      width: 55px;
      min-width: 55px;
      @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
        height: 40px;
        width: 40px;
        min-width: 40px;
      }
    }
  }
  .details {
    padding-left: 15px;
    .article_link,
    .article_title,
    .recipient-count {
      display: -webkit-box;
      -webkit-box-orient: vertical;
      overflow: hidden;
      text-overflow: ellipsis;
      margin-bottom: 0px;
      -webkit-box-align: start;
      width: 100%;
      word-break: break-word;
    }
    .article_title {
      -webkit-line-clamp: 1;
      font-family: "Roboto", sans-serif;
      font-weight: 500;
      font-size: 14px;
      line-height: 16px;
      color: #424b50;
      @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
        line-height: 18px;
        font-size: 9px;
      }
    }
    .article_link {
      -webkit-line-clamp: 1;
      font-family: "Roboto", sans-serif;
      font-weight: 500;
      line-height: 16px;
      font-size: 14px;
      color: #27ce0a;
      @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
        line-height: 18px;
        font-size: 9px;
      }
    }
  }
  .actions {
    -ms-flex-negative: 1;
    flex-shrink: 1;
    align-self: center;
    margin-left: auto;
    min-width: 106px;
    ul {
      list-style: none;
      margin: 0;
      padding: 0;
      display: flex;
      width: 100%;
      li {
        float: left;
        display: inline-block;
        padding: 0px;
        &.clicks {
          padding-right: 10px;
          .container-span {
            font-family: "Roboto", sans-serif;
            color: #fff;
            font-size: 16px;
            font-weight: 500;
            background-color: #1971eb;
            border-radius: 12px;
            padding: 0px 10px;
            display: inline-flex;
            width: 80px;
            height: 22px;
            justify-content: center;
            align-items: center;
            @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
              font-size: 11px;
              width: 70px;
            }
            svg {
              vertical-align: baseline;
              margin-right: 5px;
            }
          }
        }
        &.card-popover {
          a {
            padding: 0 5px;
            svg {
              width: 5px;
              max-height: none;
              @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
                width: 3px;
              }
            }
          }
        }
      }
    }
  }
`;
